import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import CrmEventsAPI from 'dashboard/api/crmEvents';

const POLL_INTERVAL = 30_000; // 30 seconds
const NOTIFICATION_DURATION = 5_000; // 5 seconds
const AUDIO_SRC = '/audio/dashboard/bell.mp3';

export function useAgendaNotifications() {
  const getters = useStoreGetters();
  const uiSettings = computed(() => getters.getUISettings.value);
  const currentUser = computed(() => getters.getCurrentUser.value);
  const currentAccountId = computed(
    () => getters.getCurrentAccountId.value
  );

  const notifications = ref([]);
  const notifiedEventIds = ref(new Set());
  let pollTimer = null;
  let audio = null;

  function initAudio() {
    if (!audio) {
      audio = new Audio(AUDIO_SRC);
      audio.load();
    }
  }

  function playSound() {
    if (audio) {
      audio.currentTime = 0;
      const result = audio.play();
      if (result && result.catch) {
        result.catch(() => {});
      }
    }
  }

  function getWindowLabel(label) {
    const labels = {
      '1_day': '1 dia',
      '1_hour': '1 hora',
      '5_min': '5 minutos',
    };
    return labels[label] || label;
  }

  function dismissNotification(id) {
    notifications.value = notifications.value.filter(n => n.id !== id);
  }

  function autoDismiss(id) {
    setTimeout(() => {
      dismissNotification(id);
    }, NOTIFICATION_DURATION);
  }

  async function checkNotifications() {
    if (!currentAccountId.value || !currentUser.value?.id) return;

    const notify1Day = uiSettings.value.crm_agenda_notify_1_day !== false;
    const notify1Hour = uiSettings.value.crm_agenda_notify_1_hour !== false;
    const notify5Min = uiSettings.value.crm_agenda_notify_5_min !== false;

    if (!notify1Day && !notify1Hour && !notify5Min) return;

    try {
      const { data } = await CrmEventsAPI.notificationsDue({
        notify_1_day: notify1Day,
        notify_1_hour: notify1Hour,
        notify_5_min: notify5Min,
      });

      const events = data.payload || [];
      events.forEach(event => {
        const uniqueKey = `${event.id}_${event.notification_window}`;
        if (notifiedEventIds.value.has(uniqueKey)) return;

        notifiedEventIds.value.add(uniqueKey);
        const notif = {
          id: uniqueKey,
          eventId: event.id,
          title: event.title,
          startsAt: event.starts_at,
          location: event.location,
          windowLabel: getWindowLabel(event.notification_window),
          contactName: event.contact?.name,
        };
        notifications.value.push(notif);
        playSound();
        autoDismiss(uniqueKey);
      });
    } catch {
      // silently ignore polling errors
    }
  }

  function startPolling() {
    initAudio();
    checkNotifications();
    pollTimer = setInterval(checkNotifications, POLL_INTERVAL);
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  onMounted(() => {
    if (currentAccountId.value) {
      startPolling();
    }
  });

  watch(currentAccountId, newVal => {
    stopPolling();
    notifiedEventIds.value.clear();
    notifications.value = [];
    if (newVal) {
      startPolling();
    }
  });

  onUnmounted(() => {
    stopPolling();
  });

  return {
    notifications,
    dismissNotification,
  };
}
